import React from 'react'
import { useTranslation } from 'react-i18next'
import { Timeline, TimelineProps } from '@node-space/ui-components'
import { Application, CustomerApplicationDetails } from 'types/customers'
import {
  CUSTOMER_STATUS_AGREEMENTS_PENDING,
  CUSTOMER_STATUS_VERIFIED,
  REQUIRED_ACTION_CATEGORY_ASSOCIATES,
  REQUIRED_ACTION_CATEGORY_COMPANY_INFORMATION,
  REQUIRED_ACTION_CATEGORY_DOCUMENT_REQUIREMENTS,
  REQUIRED_ACTION_CATEGORY_NATURE_OF_BUSINESS,
} from '../helpers/constants'

interface CustomerOnboardingTimelineProps {
  application: Application | undefined
  data: CustomerApplicationDetails | undefined
  isV1Customer: boolean
  isIndividual?: boolean
  isReliance?: boolean
  hasUploadedDocuments?: boolean
}

export const CustomerOnboardingTimeline = ({
  application,
  data,
  isV1Customer,
  isIndividual,
  isReliance,
  hasUploadedDocuments,
}: CustomerOnboardingTimelineProps) => {
  const { t } = useTranslation()

  if (isV1Customer || isIndividual) return null

  const status = application?.status
  const requiredActions = data?.requiredActions ?? []

  const isPendingAgreements = status === CUSTOMER_STATUS_AGREEMENTS_PENDING
  const isVerified = status === CUSTOMER_STATUS_VERIFIED

  const getIsPendingCategory = (category: string) => {
    if (isPendingAgreements) return true
    return requiredActions.some(action => action?.category === category)
  }

  const hasDocumentRequirements = requiredActions.some(
    action => action?.category === REQUIRED_ACTION_CATEGORY_DOCUMENT_REQUIREMENTS
  )
  const showUploadDocuments = !isReliance || hasDocumentRequirements || hasUploadedDocuments

  const items: TimelineProps['items'] = [
    {
      Title: t('customers.timeline.agreements'),
      description: t('customers.timeline.agreementsDescription'),
      variant: isPendingAgreements ? 'warning' : 'success',
    },
    ...(showUploadDocuments
      ? [
          {
            Title: t('customers.timeline.uploadDocuments'),
            description: t('customers.timeline.uploadDocumentsDescription'),
            variant: getIsPendingCategory(REQUIRED_ACTION_CATEGORY_DOCUMENT_REQUIREMENTS)
              ? ('warning' as const)
              : ('success' as const),
          },
        ]
      : []),
    {
      Title: t('customers.timeline.companyInformation'),
      description: t('customers.timeline.companyInformationDescription'),
      variant: getIsPendingCategory(REQUIRED_ACTION_CATEGORY_COMPANY_INFORMATION)
        ? 'warning'
        : 'success',
    },
    {
      Title: t('customers.timeline.natureOfBusiness'),
      description: t('customers.timeline.natureOfBusinessDescription'),
      variant: getIsPendingCategory(REQUIRED_ACTION_CATEGORY_NATURE_OF_BUSINESS)
        ? 'warning'
        : 'success',
    },
    {
      Title: t('customers.timeline.associates'),
      description: t('customers.timeline.associatesDescription'),
      variant: getIsPendingCategory(REQUIRED_ACTION_CATEGORY_ASSOCIATES) ? 'warning' : 'success',
    },
    {
      Title: t('customers.timeline.companyVerification'),
      variant: isPendingAgreements || !isVerified ? 'warning' : 'success',
    },
  ]

  return <Timeline items={items} />
}
